import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  updateDoc,
  deleteDoc,
  doc,
  writeBatch,
  Timestamp,
} from "firebase/firestore";
import { formatDate } from "../utils/formatDate";
import { getUserProfile } from "../utils/userProfile";
import Avatar from "./UserAvatar";
import ConfirmModal from "./ConfirmModal";

interface Notification {
  id: string;
  recipientUid: string;
  fromUid: string;
  fromName: string;
  commentId?: string;
  parentCommentId?: string;
  postId: string;
  postTitle: string;
  type: "reply" | "mention" | "comment_deleted" | "reply_cascade_deleted" | "new_comment" | "role_changed" | "comment_permission_changed" | "post_reaction" | "welcome" | "bookmark" | "reply_edited" | "new_post" | "new_follower" | "profile_viewed" | "newsletter_subscribed";
  oldRole?: string;
  newRole?: string;
  canComment?: boolean;
  reactionType?: string;
  reason?: string;
  read: boolean;
  createdAt: Timestamp;
}

const getNotificationMessage = (notification: Notification): string => {
  switch (notification.type) {
    case "welcome":
      return "Welcome to the platform! Thrilled to have you here. Thank you for joining - explore posts, share your thoughts, and personalize your profile to get started.";
    case "newsletter_subscribed":
      return "Thanks for subscribing to our newsletter! 🎉 You'll now get email updates whenever a writer publishes a new post. Stay tuned for fresh content from our writing team!";
    case "mention":
      return "mentioned you in a comment on:";
    case "comment_deleted":
      return "deleted your comment on:";
    case "reply_cascade_deleted":
      return "deleted a comment you replied to (your reply was also removed). This comment was on:";
    case "new_comment":
      return "commented on your post:";
    case "role_changed":
      return `changed your role from ${notification.oldRole || "none"} to ${notification.newRole}`;
    case "comment_permission_changed":
      return notification.canComment
        ? "enabled your ability to comment"
        : "restricted you from commenting";
    case "post_reaction":
      return `reacted with ${notification.reactionType} to your post:`;
    case "bookmark":
      return "saved your post to bookmarks:";
    case "reply_edited":
      return "edited their reply to your comment on:";
    case "new_post":
      return "published a new post:";
    case "new_follower":
      return "started following you! 🎉";
    case "profile_viewed":
      return "viewed your profile 👀";
    case "reply":
    default:
      return "replied to your comment on:";
  }
};

const NotificationBell: React.FC = () => {
  const auth = getAuth();
  const currentUser = auth.currentUser;
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [displayNames, setDisplayNames] = useState<{ [uid: string]: string }>({});
  const [photoURLs, setPhotoURLs] = useState<{ [uid: string]: string | null }>({});
  const [emails, setEmails] = useState<{ [uid: string]: string }>({});
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const prevUnreadCountRef = useRef<number>(0);
  const isInitialLoadRef = useRef(true);

  // Play notification sound
  const playNotificationSound = () => {
    const audio = new Audio("/assets/sounds/notification.mp3");
    audio.volume = 0.5;
    audio.play().catch(() => { });
  };

  // Real-time listener for notifications
  useEffect(() => {
    if (!currentUser) return;

    const q = query(
      collection(db, "notifications"),
      where("recipientUid", "==", currentUser.uid),
      orderBy("createdAt", "desc"),
      limit(20)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedNotifications = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Notification[];

      const newUnreadCount = fetchedNotifications.filter((n) => !n.read).length;

      // Play sound if new unread notifications arrived (not on initial load)
      if (!isInitialLoadRef.current && newUnreadCount > prevUnreadCountRef.current) {
        playNotificationSound();
      }

      prevUnreadCountRef.current = newUnreadCount;
      isInitialLoadRef.current = false;
      setNotifications(fetchedNotifications);
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Fetch display names and photos for notification senders
  useEffect(() => {
    const fetchUserData = async () => {
      const uniqueUids = Array.from(new Set(notifications.map((n) => n.fromUid)));
      const names: { [uid: string]: string } = {};
      const photos: { [uid: string]: string | null } = {};
      const userEmails: { [uid: string]: string } = {};

      await Promise.all(
        uniqueUids.map(async (uid) => {
          try {
            const profile = await getUserProfile(uid);
            if (profile?.displayName) {
              names[uid] = profile.displayName;
            }
            photos[uid] = profile?.photoURL || null;
            if (profile?.email) {
              userEmails[uid] = profile.email;
            }
          } catch (error) {
            console.error(`Error fetching profile for ${uid}:`, error);
          }
        })
      );

      setDisplayNames(names);
      setPhotoURLs(photos);
      setEmails(userEmails);
    };

    if (notifications.length > 0) {
      fetchUserData();
    }
  }, [notifications]);

  const closeDropdown = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, 150);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        if (isOpen && !isClosing) {
          closeDropdown();
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, isClosing]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const markAsRead = async (notificationId: string) => {
    try {
      await updateDoc(doc(db, "notifications", notificationId), {
        read: true,
      });
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const batch = writeBatch(db);
      notifications
        .filter((n) => !n.read)
        .forEach((n) => {
          batch.update(doc(db, "notifications", n.id), { read: true });
        });
      await batch.commit();
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    closeDropdown();
  };

  const deleteNotification = async (e: React.MouseEvent, notificationId: string) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await deleteDoc(doc(db, "notifications", notificationId));
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  const deleteAllNotifications = async () => {
    try {
      const batch = writeBatch(db);
      notifications.forEach((n) => {
        batch.delete(doc(db, "notifications", n.id));
      });
      await batch.commit();
      setShowDeleteAllModal(false);
    } catch (error) {
      console.error("Error deleting all notifications:", error);
    }
  };

  if (!currentUser) return null;

  return (
    <div className="notification-bell" ref={dropdownRef}>
      <button
        className="notification-bell__button"
        onClick={() => isOpen ? closeDropdown() : setIsOpen(true)}
        aria-label="Notifications"
      >
        <span className="material-symbols-outlined notranslate">notifications</span>
        {unreadCount > 0 && (
          <span className="notification-bell__badge">{unreadCount > 9 ? "9+" : unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div className={`notification-bell__dropdown ${isClosing ? 'notification-bell__dropdown--closing' : ''}`}>
          <div className="notification-bell__header">
            <h3>Notifications</h3>
            <div className="notification-bell__header-actions">
              {unreadCount > 0 && (
                <button
                  className="notification-bell__mark-all"
                  onClick={markAllAsRead}
                >
                  Mark all as read
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  className="notification-bell__delete-all"
                  onClick={() => setShowDeleteAllModal(true)}
                  title="Delete all notifications"
                >
                  <span className="material-symbols-outlined notranslate">delete_sweep</span>
                </button>
              )}
            </div>
          </div>

          <div className="notification-bell__list">
            {notifications.length === 0 ? (
              <div className="notification-bell__empty">
                <span className="material-symbols-outlined notranslate">inbox</span>
                <p>No notifications yet</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <Link
                  key={notification.id}
                  to={notification.type === "welcome" || notification.type === "newsletter_subscribed"
                    ? "/"
                    : notification.type === "role_changed" || notification.type === "comment_permission_changed"
                      ? `/profile/${notification.recipientUid}`
                      : notification.type === "new_follower" || notification.type === "profile_viewed"
                        ? `/profile/${notification.fromUid}`
                        : notification.commentId
                          ? `/post/${notification.postId}#${notification.commentId}`
                          : `/post/${notification.postId}`}
                  className={`notification-bell__item ${!notification.read ? "notification-bell__item--unread" : ""}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notification-bell__avatar">
                    <Avatar
                      email={emails[notification.fromUid] || ""}
                      photoURL={photoURLs[notification.fromUid]}
                      className="notification-bell__avatar-img"
                    />
                  </div>
                  <div className="notification-bell__content">
                    <p className="notification-bell__message">
                      <strong>{displayNames[notification.fromUid] || notification.fromName}</strong>
                      <span className="notification-bell__action">
                        {getNotificationMessage(notification)}
                      </span>
                    </p>
                    {notification.postTitle && (
                      <p className="notification-bell__post">
                        "{notification.postTitle}"
                      </p>
                    )}
                    {notification.reason && (
                      <p className="notification-bell__reason">
                        Reason: {notification.reason}
                      </p>
                    )}
                    <span className="notification-bell__time">
                      {notification.createdAt?.toDate
                        ? formatDate(notification.createdAt.toDate())
                        : "Just now"}
                    </span>
                  </div>
                  <div className="notification-bell__actions">
                    {!notification.read && (
                      <span className="notification-bell__dot" />
                    )}
                    <button
                      className="notification-bell__delete"
                      onClick={(e) => deleteNotification(e, notification.id)}
                      title="Delete notification"
                    >
                      <span className="material-symbols-outlined notranslate">close</span>
                    </button>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      )}

      {showDeleteAllModal && (
        <ConfirmModal
          title="Delete All Notifications"
          message="Are you sure you want to delete all notifications? This action cannot be undone."
          confirmText="Delete All"
          icon="delete_sweep"
          variant="danger"
          onConfirm={deleteAllNotifications}
          onCancel={() => setShowDeleteAllModal(false)}
        />
      )}
    </div>
  );
};

export default NotificationBell;
